import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  MapPin, 
  CheckCircle2, 
  Circle,
  Building2, 
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Search,
  Map,
  ArrowRight,
  Play
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PostcodeDistricts = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRegions, setExpandedRegions] = useState(new Set());

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/postcode-districts`, { headers });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch postcode districts:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const toggleRegion = (regionName) => {
    const newExpanded = new Set(expandedRegions);
    if (newExpanded.has(regionName)) {
      newExpanded.delete(regionName);
    } else {
      newExpanded.add(regionName);
    }
    setExpandedRegions(newExpanded);
  };

  const expandAll = () => {
    if (data) {
      setExpandedRegions(new Set(data.regions.map(r => r.name)));
    }
  };

  const collapseAll = () => {
    setExpandedRegions(new Set());
  };

  const handleDistrictClick = (districtCode) => {
    // Navigate to find businesses page with the district pre-filled
    sessionStorage.setItem('prefillPostcode', districtCode);
    navigate('/admin/find');
  };

  // Find the next unsearched district in order
  const getNextUnsearched = () => {
    if (!data) return null;
    for (const region of data.regions) {
      for (const district of region.districts) {
        if (!district.searched) {
          return { district, region: region.name };
        }
      }
    }
    return null; // All districts searched!
  };

  const handleNextUnsearched = () => {
    const next = getNextUnsearched();
    if (next) {
      handleDistrictClick(next.district.code);
    }
  };

  const nextUnsearched = data ? getNextUnsearched() : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-muted-foreground py-12">
        Failed to load postcode districts data
      </div>
    );
  }

  const { summary, regions } = data;

  // Filter regions and districts based on search
  const filteredRegions = regions.map(region => {
    if (!searchTerm) return region;
    
    const filteredDistricts = region.districts.filter(d => 
      d.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (filteredDistricts.length > 0 || region.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return {
        ...region,
        districts: filteredDistricts.length > 0 ? filteredDistricts : region.districts
      };
    }
    return null;
  }).filter(Boolean);

  return (
    <div className="space-y-6" data-testid="postcode-districts">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" data-testid="districts-title">UK Postcode Districts</h2>
          <p className="text-muted-foreground">Work through districts systematically</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={expandAll}
            size="sm"
          >
            Expand All
          </Button>
          <Button 
            variant="outline" 
            onClick={collapseAll}
            size="sm"
          >
            Collapse All
          </Button>
          <Button 
            variant="outline" 
            onClick={handleRefresh} 
            disabled={refreshing}
            data-testid="refresh-districts-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Next Unsearched Quick Action */}
      {nextUnsearched && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <Play className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Next up: <span className="text-primary font-bold">{nextUnsearched.district.code}</span></p>
                  <p className="text-sm text-muted-foreground">{nextUnsearched.region}</p>
                </div>
              </div>
              <Button 
                onClick={handleNextUnsearched}
                className="gap-2"
                data-testid="next-unsearched-btn"
              >
                Search Next District
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!nextUnsearched && summary.total_searched > 0 && (
        <Card className="border-green-500/50 bg-green-500/5">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-500/10">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="font-medium text-green-600">All districts searched!</p>
                <p className="text-sm text-muted-foreground">You've covered all {summary.total_districts.toLocaleString()} UK postcode districts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="total-districts-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Map className="w-4 h-4 text-primary" />
              Total Districts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{summary.total_districts.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Across the UK</p>
          </CardContent>
        </Card>

        <Card data-testid="searched-districts-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              Searched
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-500">{summary.total_searched}</div>
            <p className="text-xs text-muted-foreground">{summary.coverage_percent}% coverage</p>
          </CardContent>
        </Card>

        <Card data-testid="remaining-districts-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Circle className="w-4 h-4 text-orange-400" />
              Remaining
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-400">{(summary.total_districts - summary.total_searched).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Districts to search</p>
          </CardContent>
        </Card>

        <Card data-testid="businesses-found-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-500" />
              Businesses Found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-500">{summary.total_businesses}</div>
            <p className="text-xs text-muted-foreground">Total opportunities</p>
          </CardContent>
        </Card>
      </div>

      {/* Overall Progress */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Overall Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <Progress value={summary.coverage_percent} className="h-3" />
          <p className="text-sm text-muted-foreground mt-2">
            {summary.total_searched} of {summary.total_districts.toLocaleString()} districts searched ({summary.coverage_percent}%)
          </p>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="flex items-center gap-2 max-w-md">
        <Search className="w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search districts (e.g., SW1, Manchester)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1"
          data-testid="district-search"
        />
        {searchTerm && (
          <Button variant="ghost" size="sm" onClick={() => setSearchTerm('')}>
            Clear
          </Button>
        )}
      </div>

      {/* Regions with Districts */}
      <div className="space-y-3">
        {filteredRegions.map((region) => {
          const isExpanded = expandedRegions.has(region.name);
          const progressPercent = region.total > 0 ? Math.round((region.searched / region.total) * 100) : 0;
          
          return (
            <Card key={region.name} className="overflow-hidden">
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => toggleRegion(region.name)}
                data-testid={`region-${region.name.replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <h3 className="font-semibold">{region.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {region.searched}/{region.total} searched • {region.businesses_found} businesses
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-32">
                    <Progress value={progressPercent} className="h-2" />
                  </div>
                  <Badge 
                    variant={progressPercent === 100 ? "default" : progressPercent > 0 ? "secondary" : "outline"}
                    className={progressPercent === 100 ? "bg-green-500" : ""}
                  >
                    {progressPercent}%
                  </Badge>
                </div>
              </div>
              
              {isExpanded && (
                <CardContent className="pt-0 pb-4">
                  <div className="flex flex-wrap gap-2 mt-2 border-t pt-4">
                    {region.districts.map((district) => (
                      <Button
                        key={district.code}
                        variant={district.searched ? "default" : "outline"}
                        size="sm"
                        className={`
                          relative group
                          ${district.searched 
                            ? district.businesses_found > 0 
                              ? 'bg-green-600 hover:bg-green-700 text-white' 
                              : 'bg-gray-500 hover:bg-gray-600 text-white'
                            : 'hover:bg-primary/10'
                          }
                        `}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDistrictClick(district.code);
                        }}
                        title={district.searched 
                          ? `${district.businesses_found} businesses found - Click to search again`
                          : 'Click to search this district'
                        }
                        data-testid={`district-${district.code}`}
                      >
                        {district.searched && (
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                        )}
                        {district.code}
                        {district.searched && district.businesses_found > 0 && (
                          <span className="ml-1 text-xs opacity-75">
                            ({district.businesses_found})
                          </span>
                        )}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {filteredRegions.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <MapPin className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">No districts match your search</p>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <Card className="bg-muted/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Button size="sm" className="bg-green-600 hover:bg-green-600 pointer-events-none h-7">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                SW1 (5)
              </Button>
              <span className="text-muted-foreground">Searched - has businesses</span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" className="bg-gray-500 hover:bg-gray-500 pointer-events-none h-7">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                SW2
              </Button>
              <span className="text-muted-foreground">Searched - no businesses found</span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="pointer-events-none h-7">
                SW3
              </Button>
              <span className="text-muted-foreground">Not yet searched</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PostcodeDistricts;
